import User, { UserOptions } from 'models/User';

import AIArtifact, { AIArtifactOptions } from './AIArtifact';

export enum AIJobStatus {
  AWAITING = 'awaiting',
  COMPLETED = 'completed',
  ERRORED = 'error',
  PENDING = 'pending',
}

export interface AIJobOptions {
  id: string;
  type: string;
  reviewId?: string;
  aiArtifact?: AIArtifactOptions;
  aiJobbableId: string;
  createdBy?: UserOptions;
  createdByGroup?: {
    id: string;
    name: string;
  };
  config?: {
    attachmentIds?: string[];
  };
  createdAt?: Date | null;
  updatedAt?: Date | null;
  status?: AIJobStatus;
  hasCollapsedPromptExplainer?: boolean;
}

/**
 * @class AIJob
 * @classdesc Represents an AI job in the system
 * @property {string} id - The job's id
 * @property {string} type - The job's type
 * @property {string | null} reviewId - The associated review id
 * @property {AIArtifact | null} aiArtifact - The associated AI artifact
 * @property {Partial<User> | null} createdBy - The user who created the job
 * @property {Object | null} createdByGroup - The group that created the job
 * @property {Date | null} createdAt - The job's creation date
 * @property {AIJobStatus | null} status - The job's status
 * @property {boolean | null} hasCollapsedPromptExplainer - Whether the job's prompt explainer has been collapsed
 * @param {Partial<AIJobOptions>} [options={}]
 */
export default class AIJob {
  id: string;
  type: string;
  reviewId: string | null;
  aiArtifact: AIArtifact | null;
  aiJobbableId: string | null;
  createdBy: Partial<User> | null;
  createdByGroup: {
    id: string;
    name: string;
  } | null;
  config: {
    attachmentIds?: string[];
  } | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  status: AIJobStatus | null;
  hasCollapsedPromptExplainer: boolean | null;

  constructor(options?: Partial<AIJobOptions>) {
    const opts = { ...options };
    this.id = opts.id || '';
    this.type = opts.type || '';
    this.reviewId = opts.reviewId || null;
    this.aiArtifact = opts.aiArtifact ? new AIArtifact(opts.aiArtifact) : null;
    this.aiJobbableId = opts.aiJobbableId || null;
    this.createdBy = opts.createdBy ? new User(opts.createdBy) : null;
    this.createdByGroup = opts.createdByGroup || null;
    this.config = opts.config || null;
    this.createdAt = opts.createdAt || null;
    this.updatedAt = opts.updatedAt || null;
    this.status = opts.status || null;
    this.hasCollapsedPromptExplainer = opts.hasCollapsedPromptExplainer ?? null;
  }

  get attachmentIds() {
    return this.config?.attachmentIds || [];
  }

  get isAwaiting() {
    return this.status === AIJobStatus.AWAITING;
  }

  get isPending() {
    return this.status === AIJobStatus.PENDING;
  }

  get isProcessing() {
    return this.isAwaiting || this.isPending;
  }

  get isCompleted() {
    return this.status === AIJobStatus.COMPLETED;
  }

  get isErrored() {
    return this.status === AIJobStatus.ERRORED;
  }

  serialize() {
    return {
      id: this.id,
      type: this.type,
      reviewId: this.reviewId,
      aiArtifact: this.aiArtifact?.serialize() || null,
      aiJobbableId: this.aiJobbableId,
      createdBy: this.createdBy,
      createdByGroup: this.createdByGroup,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      status: this.status,
      hasCollapsedPromptExplainer: this.hasCollapsedPromptExplainer,
    };
  }
}
